import { idadeEmMeses, type Classe, type FaixaDoPotro } from '@equiview/domain';
import { classeNaData, faixaNaData, ROTULO_CLASSE_PLURAL, ROTULO_FAIXA, ROTULO_SEXO } from './catalogo.js';
import type { Animal, Avaliacao, Haras } from './fonte.js';

/**
 * Plantel como dado: uma funcao pura monta as linhas, outra filtra, outra
 * ordena, outra resume. A pagina so desenha. Indicadores, graficos e lista
 * leem o MESMO recorte, entao nunca discordam entre si.
 */

/** Em dia: avaliado nos ultimos 12 meses. Vencida: so antes disso. Nunca: nenhuma ficha. */
export type SituacaoAvaliacao = 'em-dia' | 'vencida' | 'nunca';
/** Filtro de avaliacao: uma situacao, ou "pendente" (vencida ou nunca). */
export type FiltroAvaliacao = SituacaoAvaliacao | 'pendente';
export type OrdemPlantel = 'nome' | 'idade' | 'avaliacao' | 'avaliacoes';
export type Marcha = NonNullable<Animal['marcha_type_declared']>;

export interface LinhaPlantel {
  animal: Animal;
  classe: Classe;
  faixa: FaixaDoPotro | null;
  meses: number;
  ultimaAvaliacao: string | null;
  primeiraAvaliacao: string | null;
  avaliacoes: number;
  /** Maior nivel de protocolo ja colhido: 0 sem ficha, 1 so censo, 2+ completa. */
  nivelMaximo: 0 | 1 | 2 | 3;
  situacao: SituacaoAvaliacao;
}

export interface FiltrosPlantel {
  classe: Classe | null;
  faixa: FaixaDoPotro | null;
  q: string;
  sexo: Animal['sex'] | '';
  pelagem: string;
  marcha: Marcha | '';
  avaliacao: FiltroAvaliacao | '';
}

export const SEM_FILTRO: FiltrosPlantel = {
  classe: null,
  faixa: null,
  q: '',
  sexo: '',
  pelagem: '',
  marcha: '',
  avaliacao: '',
};

export const ROTULO_SITUACAO: Record<SituacaoAvaliacao, string> = {
  'em-dia': 'Em dia',
  vencida: 'Vencida',
  nunca: 'Nunca avaliado',
};
export const ROTULO_FILTRO_AVALIACAO: Record<FiltroAvaliacao, string> = {
  'em-dia': 'Em dia (últimos 12 meses)',
  pendente: 'Pendente (vencida ou nunca)',
  vencida: 'Vencida (há mais de 12 meses)',
  nunca: 'Nunca avaliado',
};
export const ROTULO_MARCHA: Record<Marcha, string> = { batida: 'Batida', picada: 'Picada' };

/** Mesma data, um ano antes (29/02 vira 28/02 por comparacao de texto, o que basta aqui). */
export const umAnoAntes = (dia: string) => `${Number(dia.slice(0, 4)) - 1}${dia.slice(4)}`;

export function situacaoNaData(ultima: string | null, hoje: string): SituacaoAvaliacao {
  if (!ultima) return 'nunca';
  return ultima >= umAnoAntes(hoje) ? 'em-dia' : 'vencida';
}

/** Linhas do plantel do haras atual (pais de fora da base, de outro dono, nao entram). */
export function montarLinhas(
  animais: readonly Animal[],
  avaliacoes: readonly Avaliacao[],
  haras: Pick<Haras, 'id'> | null,
  hoje: string,
): LinhaPlantel[] {
  const porAnimal = new Map<string, Avaliacao[]>();
  for (const a of avaliacoes) {
    const l = porAnimal.get(a.animal_id);
    if (l) l.push(a);
    else porAnimal.set(a.animal_id, [a]);
  }
  return animais
    .filter((a) => !haras || a.owner_farm_id === haras.id)
    .map((animal) => {
      const fichas = porAnimal.get(animal.id) ?? [];
      const datas = fichas.map((f) => f.visit_date).sort();
      const classe = classeNaData(animal, animais, hoje);
      const ultima = datas.at(-1) ?? null;
      return {
        animal,
        classe,
        faixa: classe === 'potro' ? faixaNaData(animal, hoje) : null,
        meses: idadeEmMeses(animal.birth_date, hoje),
        ultimaAvaliacao: ultima,
        primeiraAvaliacao: datas[0] ?? null,
        avaliacoes: fichas.length,
        nivelMaximo: fichas.reduce<0 | 1 | 2 | 3>((m, f) => (f.protocol_level > m ? f.protocol_level : m), 0),
        situacao: situacaoNaData(ultima, hoje),
      };
    });
}

const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR');

export function casaAvaliacao(s: SituacaoAvaliacao, f: FiltroAvaliacao | ''): boolean {
  if (!f) return true;
  return f === 'pendente' ? s !== 'em-dia' : s === f;
}

/**
 * Filtra pelo conjunto de filtros. `ignorar` deixa um filtro de fora: e o que
 * as contagens das abas de classe usam (contam cada classe com os OUTROS
 * filtros valendo).
 */
export function filtrarPlantel(
  linhas: readonly LinhaPlantel[],
  f: FiltrosPlantel,
  ignorar: ReadonlyArray<keyof FiltrosPlantel> = [],
): LinhaPlantel[] {
  const vale = (k: keyof FiltrosPlantel) => !ignorar.includes(k);
  const termo = normalizar(f.q.trim());
  return linhas.filter(
    (l) =>
      (!vale('classe') || !f.classe || l.classe === f.classe) &&
      (!vale('faixa') || !f.faixa || l.faixa === f.faixa) &&
      (!vale('sexo') || !f.sexo || l.animal.sex === f.sexo) &&
      (!vale('pelagem') || !f.pelagem || normalizar(l.animal.coat_color ?? '') === normalizar(f.pelagem)) &&
      (!vale('marcha') || !f.marcha || l.animal.marcha_type_declared === f.marcha) &&
      (!vale('avaliacao') || casaAvaliacao(l.situacao, f.avaliacao)) &&
      (!vale('q') ||
        !termo ||
        normalizar(l.animal.name).includes(termo) ||
        normalizar(l.animal.abccmm_registry ?? '').includes(termo)),
  );
}

/** Quantos filtros estao valendo (a classe conta; a faixa so existe dentro de potro). */
export function filtrosAtivos(f: FiltrosPlantel): number {
  return [f.classe, f.faixa, f.q.trim(), f.sexo, f.pelagem, f.marcha, f.avaliacao].filter(Boolean).length;
}

/**
 * Ordena sem mexer no original. Desempate sempre pelo nome: a ordem nao pula
 * entre cliques. Sem avaliacao vai para o FIM nas duas direcoes: "nunca" nao e
 * a avaliacao mais antiga nem a mais recente.
 */
export function ordenarPlantel(
  linhas: readonly LinhaPlantel[],
  ordem: OrdemPlantel,
  dir: 'asc' | 'desc',
): LinhaPlantel[] {
  const s = dir === 'asc' ? 1 : -1;
  const porNome = (a: LinhaPlantel, b: LinhaPlantel) =>
    a.animal.name.localeCompare(b.animal.name, 'pt-BR') || a.animal.id.localeCompare(b.animal.id);
  return [...linhas].sort((a, b) => {
    if (ordem === 'avaliacao') {
      if (!a.ultimaAvaliacao || !b.ultimaAvaliacao) {
        if (a.ultimaAvaliacao === b.ultimaAvaliacao) return porNome(a, b);
        return a.ultimaAvaliacao ? -1 : 1;
      }
      return s * a.ultimaAvaliacao.localeCompare(b.ultimaAvaliacao) || porNome(a, b);
    }
    if (ordem === 'idade') return s * (a.meses - b.meses) || porNome(a, b);
    if (ordem === 'avaliacoes') return s * (a.avaliacoes - b.avaliacoes) || porNome(a, b);
    return s * porNome(a, b);
  });
}

export interface ResumoPlantel {
  total: number;
  emDia: number;
  vencida: number;
  nunca: number;
  potros: number;
  ultimaAvaliacao: string | null;
  porClasse: Record<Classe, number>;
  porFaixa: Record<FaixaDoPotro, number>;
}

/** Os numeros dos indicadores e graficos, sempre do recorte que a lista mostra. */
export function resumirPlantel(linhas: readonly LinhaPlantel[]): ResumoPlantel {
  const r: ResumoPlantel = {
    total: linhas.length,
    emDia: 0,
    vencida: 0,
    nunca: 0,
    potros: 0,
    ultimaAvaliacao: null,
    porClasse: { potro: 0, garanhao: 0, egua: 0, castrado: 0 },
    porFaixa: { '0-12': 0, '12-24': 0, '24-36': 0 },
  };
  for (const l of linhas) {
    if (l.situacao === 'em-dia') r.emDia++;
    else if (l.situacao === 'vencida') r.vencida++;
    else r.nunca++;
    r.porClasse[l.classe]++;
    if (l.classe === 'potro') r.potros++;
    if (l.faixa) r.porFaixa[l.faixa]++;
    if (l.ultimaAvaliacao && (!r.ultimaAvaliacao || l.ultimaAvaliacao > r.ultimaAvaliacao))
      r.ultimaAvaliacao = l.ultimaAvaliacao;
  }
  return r;
}

/** O filtro em linguagem simples, para a faixa de contexto. Vazio sem filtro. */
export function descreverFiltros(f: FiltrosPlantel): string[] {
  const p: string[] = [];
  if (f.classe)
    p.push(
      `Só ${ROTULO_CLASSE_PLURAL[f.classe].toLocaleLowerCase('pt-BR')}${f.faixa ? ` de ${ROTULO_FAIXA[f.faixa]}` : ''}`,
    );
  if (f.sexo) p.push(`sexo ${ROTULO_SEXO[f.sexo].toLocaleLowerCase('pt-BR')}`);
  if (f.pelagem) p.push(`pelagem ${f.pelagem.toLocaleLowerCase('pt-BR')}`);
  if (f.marcha) p.push(`marcha declarada ${ROTULO_MARCHA[f.marcha].toLocaleLowerCase('pt-BR')}`);
  if (f.avaliacao) p.push(`avaliação ${ROTULO_FILTRO_AVALIACAO[f.avaliacao].toLocaleLowerCase('pt-BR')}`);
  if (f.q.trim()) p.push(`busca “${f.q.trim()}”`);
  return p;
}
